import React, { useEffect, useState } from 'react';
import { ChevronDown, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink, PRODUCT_NAV } from '../../data/categoryNav';
type NavItem = {
  label: string;
  fullLabel: string;
  href: string;
  items?: NavLink[];
  /** Renders the dropdown in two columns for longer category lists. */
  wide?: boolean;
  alignRight?: boolean;
  highlight?: boolean;
};
const SERVICES_ITEMS: NavLink[] = [{
  label: 'Services',
  href: '/Services.html'
}, {
  label: 'Install Instructions',
  href: '/13.html'
}, {
  label: 'USPS Resources',
  href: '/USPSResources.html'
}, {
  label: 'Catalog',
  href: '/29.html'
}, {
  label: 'Product Option Patterns',
  href: '/product-option-patterns.html'
}, {
  label: 'Our Policies',
  href: '/OurPolicies'
}, {
  label: 'Dominion Valley Community',
  href: '/3948.html'
}];
const ABOUT_ITEMS: NavLink[] = [{
  label: 'About Us',
  href: '/AboutMidAtlanticMailboxInc.html'
}, {
  label: 'Our Gallery',
  href: '/3949.html'
}];
const NAV_ITEMS: NavItem[] = [...PRODUCT_NAV.map(group => ({
  label: group.label,
  fullLabel: group.fullLabel,
  href: group.href,
  items: group.items,
  wide: group.items.length > 6
})), {
  label: 'Services & Resources',
  fullLabel: 'Services & Resources',
  href: '/Services.html',
  items: SERVICES_ITEMS,
  alignRight: true
}, {
  label: 'About',
  fullLabel: 'About',
  href: '/AboutMidAtlanticMailboxInc.html',
  items: ABOUT_ITEMS,
  alignRight: true
}, {
  label: 'Help Choosing',
  fullLabel: 'Help Choosing',
  href: '/ContactUs.html',
  highlight: true
}];
const MOBILE_GROUPS: NavItem[] = [...NAV_ITEMS.filter(item => item.items), {
  label: 'Help Choosing',
  fullLabel: 'Help Choosing',
  href: '/ContactUs.html',
  items: [{
    label: 'Contact / FAQs',
    href: '/ContactUs.html'
  }]
}];
export function Navigation() {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>('Mailboxes');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        setActiveMenu(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const handleBlur = (e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setActiveMenu(null);
    }
  };
  return <>
      {/* Mobile menu trigger bar */}
      <div className="lg:hidden bg-brand-charcoal text-white px-4 py-3 flex justify-between items-center relative z-40" data-id="element-602" data-mp="pdp4discikiy">
        <span className="font-serif font-bold" data-id="element-603" data-mp="1d39ow645dpuc">Shop Categories</span>
        <button type="button" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-1" aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileMenuOpen} data-id="element-604" data-mp="1hxdx0sp5g3x0">
          {mobileMenuOpen ? <X size={24} data-id="element-605" data-mp="3ccaxx17oc4wi" /> : <Menu size={24} data-id="element-606" data-mp="3caq2789mifx2" />}
        </button>
      </div>

      {/* Mobile Drawer (grouped, expandable) */}
      <AnimatePresence data-id="element-607" data-mp="37dgfw1hj6kdg">
        {mobileMenuOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="lg:hidden bg-brand-charcoal text-white overflow-hidden absolute w-full max-w-full z-30 shadow-xl" data-id="element-608" data-mp="2jn3x1vkqz383">
            <div className="max-h-[75vh] overflow-y-auto py-2" data-id="element-609" data-mp="8u9j283shtwu">
              {MOBILE_GROUPS.map(group => {
            const isOpen = openGroup === group.label;
            return <div key={group.label} className="border-b border-white/10 last:border-0" data-id="element-610" data-mp="29ljsp18r5phj">
                    <button type="button" onClick={() => setOpenGroup(isOpen ? null : group.label)} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left text-base font-semibold focus:outline-none focus:bg-white/10" aria-expanded={isOpen} data-id="element-611" data-mp="2elfvwy9mlcw1">
                      <span className="min-w-0" data-id="element-612" data-mp="2xgmhj97k3sy0">{group.fullLabel}</span>
                      <ChevronDown size={18} className={`shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-613" data-mp="32a8k28k0agg3" />
                    </button>
                    <AnimatePresence data-id="element-614" data-mp="3bbf42rlv69e3">
                      {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden bg-white/5" data-id="element-615" data-mp="3ga05uvgrl14n">
                          <ul data-id="element-616" data-mp="7yw5mtwhw1kg">
                            {group.href && group.label !== 'Help Choosing' && group.label !== 'About' && group.label !== 'Services & Resources' && <li data-id="element-617" data-mp="3v9vwu90va016">
                                  <a href={group.href} className="block pl-8 pr-5 py-3 text-sm font-semibold text-brand-gold hover:text-white focus:outline-none focus:text-white" data-id="element-618" data-mp="vp0at5pc09dk">
                                    All {group.fullLabel}
                                  </a>
                                </li>}
                            {group.items?.map(item => <li key={`${group.label}-${item.label}`} data-id="element-619" data-mp="2ge422ir9y5bg">
                                <a href={item.href} className="block pl-8 pr-5 py-3.5 text-sm text-[#E2DFDD] hover:text-brand-gold hover:bg-white/5 focus:outline-none focus:text-brand-gold" data-id="element-620" data-mp="22f4ezfjetyhe">
                                  {item.label}
                                </a>
                              </li>)}
                          </ul>
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Desktop Nav — constrained to the same content rails as the header */}
      <nav className="hidden lg:block bg-brand-charcoal text-white relative z-30" data-id="element-621" data-mp="1b9fk2rjz4e0u">
        <div className="max-w-7xl mx-auto px-4" data-id="element-622" data-mp="1py3dosa9edo5">
          <ul className="flex items-center flex-nowrap w-full justify-between gap-x-0 xl:gap-x-1 text-[11.5px] xl:text-[13px] 2xl:text-sm" data-id="element-623" data-mp="1l625bhg7p73e">
            {NAV_ITEMS.map(item => {
            const hasMenu = Boolean(item.items);
            return <li key={item.label} className="relative" onMouseEnter={() => setActiveMenu(item.label)} onMouseLeave={() => setActiveMenu(null)} onFocus={() => setActiveMenu(item.label)} onBlur={handleBlur} data-id="element-624" data-mp="1uvbwmv38elqg">
                  <a href={item.href} className={`flex items-center gap-0.5 px-1.5 xl:px-2.5 2xl:px-3 py-3.5 font-medium transition-colors focus:outline-none focus:bg-white/20 whitespace-nowrap ${item.highlight ? 'bg-brand-maroon hover:bg-brand-maroonDark rounded-sm' : 'hover:bg-white/10'}`} data-id="element-625" data-mp="9i8vybvfvc11">
                    {item.label}
                    {hasMenu && <ChevronDown size={12} className={`shrink-0 transition-transform ${activeMenu === item.label ? 'rotate-180' : ''}`} data-id="element-626" data-mp="2e92z35hk5yx" />}
                  </a>

                  {item.items && <AnimatePresence data-id="element-627" data-mp="3at9m37mr04ai">
                      {activeMenu === item.label && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} transition={{
                  duration: 0.15
                }} className={`absolute top-full bg-white text-brand-charcoal shadow-xl border border-brand-border rounded-b-md overflow-hidden z-40 ${item.wide ? 'w-[520px]' : 'w-80'} ${item.alignRight ? 'right-0' : 'left-0'}`} data-id="element-628" data-mp="3fsm3vdyril68">
                          <div className="px-5 pt-4 pb-3 border-b border-brand-border/60" data-id="element-629" data-mp="1rubo6ilcayij">
                            <a href={item.href} className="block text-sm font-serif font-bold text-brand-charcoal hover:text-brand-maroon transition-colors focus:outline-none focus:text-brand-maroon" data-id="element-630" data-mp="1mx34gdkjmmf6">
                              {item.fullLabel}
                            </a>
                          </div>
                          <ul className={`py-2 ${item.wide ? 'grid grid-cols-2 gap-x-2' : ''}`} data-id="element-631" data-mp="27st829lgshnb">
                            {item.items.map(subItem => <li key={`${item.label}-${subItem.label}`} data-id="element-632" data-mp="2gt94x07pcqj7">
                                <a href={subItem.href} className="block px-5 py-2.5 text-sm text-brand-muted hover:bg-brand-panel hover:text-brand-maroon transition-colors focus:outline-none focus:bg-brand-panel focus:text-brand-maroon" data-id="element-633" data-mp="2cspcg9y2qb3e">
                                  {subItem.label}
                                </a>
                              </li>)}
                          </ul>
                        </motion.div>}
                    </AnimatePresence>}
                </li>;
          })}
          </ul>
        </div>
      </nav>
    </>;
}